import React, { useState, useEffect, useCallback } from 'react';
import QuestionCard from './QuestionCard';
import ResultsScreen from './ResultsScreen';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { logTestAccess, updateTestProgress, trackQuestionAttempts } from '../utils/testTracker';
import { getTranslatedQuestion } from '../data/translations';

const MockTestRunner = ({ test, onBack }) => {
  const { token } = useAuth();
  const { language } = useLanguage();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState(Array(test.questions.length).fill(null));
  const [showResults, setShowResults] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(test.questions.length * 90); // 90 seconds per question
  const [testStarted, setTestStarted] = useState(false);
  const [testHistoryId, setTestHistoryId] = useState(null);

  const handleOptionSelect = (optionIndex) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = optionIndex;
    setAnswers(newAnswers);
  };

  const handleNext = () => {
    if (currentQuestion < test.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleGoToQuestion = (index) => {
    setCurrentQuestion(index);
  };

  const calculateScore = useCallback(() => {
    let correct = 0;
    answers.forEach((answer, index) => {
      if (answer === test.questions[index].answerIndex) {
        correct += 1;
      }
    });
    return correct;
  }, [answers, test.questions]);

  const handleSubmitTest = useCallback(() => {
    setShowResults(true);
    if (!token || !testHistoryId) return;

    const attemptPayload = test.questions.map((question, index) => {
      const isCorrect = answers[index] === question.answerIndex;
      return {
        questionId: String(question.id || index + 1),
        attemptCount: 1,
        failureCount: isCorrect ? 0 : 1,
        successCount: isCorrect ? 1 : 0
      };
    });

    trackQuestionAttempts(
      token,
      attemptPayload,
      'mock',
      test.title,
      testHistoryId
    ).catch(() => {});

    const score = calculateScore();
    updateTestProgress(
      token,
      testHistoryId,
      score,
      test.questions.length,
      100,
      'completed'
    ).catch(() => {});
  }, [token, testHistoryId, test.questions, test.title, answers, calculateScore]);

  const handleRetake = () => {
    setCurrentQuestion(0);
    setAnswers(Array(test.questions.length).fill(null));
    setShowResults(false);
    setTimeRemaining(test.questions.length * 90);
    setTestStarted(false);
    setTestHistoryId(null);
  };

  // Timer effect
  useEffect(() => {
    if (!testStarted || showResults) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          handleSubmitTest();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [testStarted, showResults, handleSubmitTest]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const answeredCount = answers.filter(a => a !== null).length;
  useEffect(() => {
    if (!token || !testStarted || testHistoryId) return;
    logTestAccess(token, 'mock', test.title, test.category || 'Mock Tests')
      .then((history) => setTestHistoryId(history?.id || null))
      .catch(() => {});
  }, [token, testStarted, testHistoryId, test.title, test.category]);

  useEffect(() => {
    if (!token || !testStarted || !testHistoryId || showResults) return;
    if (answeredCount === 0) return;

    if (answeredCount % 5 !== 0 && answeredCount !== test.questions.length) {
      return;
    }

    const progress = Math.round((answeredCount / test.questions.length) * 100);
    updateTestProgress(
      token,
      testHistoryId,
      null,
      test.questions.length,
      progress,
      'in-progress'
    ).catch(() => {});
  }, [token, testStarted, testHistoryId, showResults, answeredCount, test.questions.length]);

  if (!testStarted) {
    const testTitle = language === 'vi' && test.title_vi ? test.title_vi : test.title;
    const testDescription = language === 'vi' && test.description_vi ? test.description_vi : test.description;
    
    return (
      <div className="test-introduction">
        <div className="intro-content">
          <h2>{testTitle}</h2>
          <p className="intro-description">{testDescription}</p>
          
          <div className="test-info">
            <div className="info-item">
              <span className="info-label">{language === 'vi' ? 'Tổng Số Câu Hỏi:' : 'Total Questions:'}</span>
              <span className="info-value">{test.questions.length}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{language === 'vi' ? 'Điểm Vượt Qua:' : 'Passing Score:'}</span>
              <span className="info-value">83% (25 {language === 'vi' ? 'câu hỏi' : 'questions'})</span>
            </div>
            <div className="info-item">
              <span className="info-label">{language === 'vi' ? 'Giới Hạn Thời Gian:' : 'Time Limit:'}</span>
              <span className="info-value">{formatTime(test.questions.length * 90)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{language === 'vi' ? 'Mỗi Câu Hỏi:' : 'Per Question:'}</span>
              <span className="info-value">~90 {language === 'vi' ? 'giây' : 'seconds'}</span>
            </div>
          </div>

          <div className="intro-instructions">
            <h3>{language === 'vi' ? 'Hướng Dẫn:' : 'Instructions:'}</h3>
            <ul>
              <li>{language === 'vi' ? 'Chọn một câu trả lời cho mỗi câu hỏi' : 'Select one answer for each question'}</li>
              <li>{language === 'vi' ? 'Bạn có thể xem lại câu trả lời trước khi gửi' : 'You can review your answers before submitting'}</li>
              <li>{language === 'vi' ? 'Bạn cần 83% (25 trên 30) đúng để vượt qua' : 'You need 83% (25 out of 30) correct to pass'}</li>
              <li>{language === 'vi' ? 'Bộ đếm thời gian sẽ đếm ngược - quản lý thời gian khôn ngoan' : 'The timer will count down - manage your time wisely'}</li>
              <li>{language === 'vi' ? 'Sau khi gửi, bạn không thể thay đổi câu trả lời của mình' : 'Once submitted, you cannot change your answers'}</li>
            </ul>
          </div>

          <div className="intro-actions">
            <button 
              className="btn btn-primary"
              onClick={() => setTestStarted(true)}
            >
              {language === 'vi' ? 'Bắt Đầu Bài Kiểm Tra' : 'Start Test'}
            </button>
            <button 
              className="btn btn-secondary"
              onClick={onBack}
            >
              {language === 'vi' ? 'Quay Lại Bài Kiểm Tra' : 'Back to Tests'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showResults) {
    const testTitle = language === 'vi' && test.title_vi ? test.title_vi : test.title;
    return (
      <ResultsScreen
        score={calculateScore()}
        totalQuestions={test.questions.length}
        testTitle={testTitle}
        answers={answers}
        questions={test.questions}
        onRetake={handleRetake}
        onBackToTests={onBack}
        language={language}
      />
    );
  }

  const currentQ = test.questions[currentQuestion];
  const translatedQ = getTranslatedQuestion(currentQ, language);

  return (
    <div className="mock-test-runner">
      <div className="runner-header">
        <div className="header-left">
          <h2>{language === 'vi' && test.title_vi ? test.title_vi : test.title}</h2>
        </div>
        <div className="header-center">
          <div className={`time-display ${timeRemaining < 300 ? 'warning' : ''}`}>
            <span className="time-icon">⏱️</span>
            {formatTime(timeRemaining)}
          </div>
        </div>
        <div className="header-right">
          <div className="answered-count">
            {answeredCount}/{test.questions.length} {language === 'vi' ? 'Đã Trả Lời' : 'Answered'}
          </div>
        </div>
      </div>

      <div className="runner-content">
        <QuestionCard
          question={{...currentQ, text: translatedQ.text, options: translatedQ.options}}
          onOptionSelect={handleOptionSelect}
          userAnswer={answers[currentQuestion]}
          currentQuestion={currentQuestion}
          totalQuestions={test.questions.length}
          language={language}
        />
      </div>

      <div className="runner-sidebar">
        <div className="question-navigator">
          <h4>{language === 'vi' ? 'Câu Hỏi' : 'Questions'}</h4>
          <div className="question-grid">
            {test.questions.map((_, index) => (
              <button
                key={index}
                className={`question-btn ${
                  index === currentQuestion ? 'active' : ''
                } ${
                  answers[index] !== null ? 'answered' : 'unanswered'
                }`}
                onClick={() => handleGoToQuestion(index)}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>

        <div className="legend">
          <div className="legend-item">
            <div className="legend-dot answered"></div>
            <span>{language === 'vi' ? 'Đã Trả Lời' : 'Answered'}</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot unanswered"></div>
            <span>{language === 'vi' ? 'Chưa Trả Lời' : 'Unanswered'}</span>
          </div>
          <div className="legend-item">
            <div className="legend-dot active"></div>
            <span>{language === 'vi' ? 'Hiện Tại' : 'Current'}</span>
          </div>
        </div>
      </div>

      <div className="runner-footer">
        <button 
          className="btn btn-secondary"
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
        >
          {language === 'vi' ? 'Câu Trước' : 'Previous'}
        </button>

        <div className="footer-actions">
          <button 
            className="btn btn-secondary"
            onClick={onBack}
          >
            {language === 'vi' ? 'Thoát Bài Kiểm Tra' : 'Exit Test'}
          </button>
          <button 
            className={`btn btn-primary ${answeredCount < test.questions.length ? 'warning' : ''}`}
            onClick={handleSubmitTest}
          >
            {answeredCount === test.questions.length 
              ? (language === 'vi' ? 'Gửi Bài Kiểm Tra' : 'Submit Test')
              : (language === 'vi' 
                  ? `Gửi Bài Kiểm Tra (${test.questions.length - answeredCount} chưa trả lời)` 
                  : `Submit Test (${test.questions.length - answeredCount} unanswered)`)}
          </button>
        </div>

        <button 
          className="btn btn-secondary"
          onClick={handleNext}
          disabled={currentQuestion === test.questions.length - 1}
        >
          {language === 'vi' ? 'Câu Tiếp Theo' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default MockTestRunner;
